import { useState } from "react";
import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import { Tabs } from "@fawkes/ui";

import { formatEuro } from "../i18n/format";
import { CrematiesList } from "./CrematiesList";
import { CrematoriumAsRetourList } from "./CrematoriumAsRetourList";
import { CrematoriumRunsList } from "./CrematoriumRunsList";

// Uitvaartoverzichten: alle dossiers, ovenruns en as-retour bij elkaar. Het waren drie schermen
// waarvan er twee niet eens in het menu stonden; als tabbladen van één overzicht zijn ze alle drie
// vindbaar, en de cijferstrip erboven zegt meteen waar het druk is.
//
// De strip komt van het dashboard-endpoint — dezelfde cijfers, zodat overzicht en dashboard elkaar
// niet kunnen tegenspreken. De url is RELATIEF; de dataProvider zet er één keer /api voor.

type TabKey = "dossiers" | "runs" | "asretour";

interface DashboardCijfers {
  ontvangen: number;
  geplandeCrematies: number;
  asKlaarVoorRetour: number;
  openstaandBedrag: number | null;
}

export function CrematoriumUitvaartOverzichten({
  onOpenDossier,
}: {
  onOpenDossier?: (id: string, relatieId?: number | null) => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [tab, setTab] = useState<TabKey>("dossiers");

  const { data } = useCustom<DashboardCijfers>({ url: "crematorium/dashboard", method: "get" });
  const cijfers = data?.data;

  const stat = (label: string, waarde: string, accent = false) => (
    <div className={`crem-stat${accent ? " crem-stat--accent" : ""}`}>
      <span className="crem-stat__label">{label}</span>
      <span className="crem-stat__value">{waarde}</span>
    </div>
  );

  return (
    <div className="crem-page">
      <div className="crem-page__head">
        <div className="crem-page__intro">
          <div>
            <h2 className="crem-page__title">{t("crematoriumOverzichten.title")}</h2>
            <p className="crem-page__sub">{t("crematoriumOverzichten.sub")}</p>
          </div>
        </div>
      </div>

      <div className="crem-strip">
        {stat(t("crematoriumOverzichten.kpi.ontvangen"), String(cijfers?.ontvangen ?? "—"))}
        {stat(t("crematoriumOverzichten.kpi.gepland"), String(cijfers?.geplandeCrematies ?? "—"))}
        {stat(t("crematoriumOverzichten.kpi.asRetour"), String(cijfers?.asKlaarVoorRetour ?? "—"))}
        {stat(
          t("crematoriumOverzichten.kpi.openstaand"),
          cijfers?.openstaandBedrag != null ? formatEuro(cijfers.openstaandBedrag, locale) : "—",
          true,
        )}
      </div>

      <Tabs
        tabs={[
          { key: "dossiers", label: t("crematoriumOverzichten.tab.dossiers") },
          { key: "runs", label: t("crematoriumOverzichten.tab.runs") },
          { key: "asretour", label: t("crematoriumOverzichten.tab.asretour") },
        ]}
        activeKey={tab}
        onChange={(key) => setTab(key as TabKey)}
        ariaLabel={t("crematoriumOverzichten.title")}
      />

      {tab === "dossiers" && <CrematiesList onOpen={onOpenDossier} />}
      {tab === "runs" && <CrematoriumRunsList />}
      {tab === "asretour" && <CrematoriumAsRetourList />}
    </div>
  );
}
